import { Link } from "react-router-dom";
import { Sparkles, Users, Home, Heart, Tag } from "lucide-react";
import { useFavorites } from "@/lib/favorites";

const pillClass =
  "inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-white/85 backdrop-blur text-accent shadow-sm";

export default function BreedCard({ breed, score, reasons, showFav = true }) {
  const { ids, toggle } = useFavorites();
  const favored = ids?.has(breed.id);

  const onFav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(breed);
  };

  return (
    <Link
      to={`/breeds/${breed.id}`}
      data-testid={`breed-card-${breed.id}`}
      className="group rounded-3xl overflow-hidden bg-card border border-border/60 warm-shadow hover:-translate-y-1 transition-transform duration-300 flex flex-col relative"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={breed.image}
          alt={breed.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 max-w-[calc(100%-1.5rem)]">
          {breed.good_with_kids >= 4 && (
            <span className={pillClass}><Users size={12} /> Kids</span>
          )}
          {breed.apartment_ok >= 4 && (
            <span className={pillClass}><Home size={12} /> Apartment</span>
          )}
          {breed.allergy_friendly >= 4 && (
            <span className={pillClass}><Heart size={12} /> Low-shed</span>
          )}
        </div>
        {typeof score === "number" && (
          <div className="absolute top-3 right-3 rounded-full bg-primary text-primary-foreground text-sm font-bold px-3 py-1 shadow-md flex items-center gap-1">
            <Sparkles size={12} />
            {score}%
          </div>
        )}
        {showFav && (
          <button
            data-testid={`fav-btn-${breed.id}`}
            onClick={onFav}
            aria-label={favored ? "Remove from favorites" : "Save to favorites"}
            className={`absolute bottom-3 right-3 w-10 h-10 rounded-full grid place-items-center backdrop-blur transition-colors ${
              favored ? "bg-primary text-primary-foreground" : "bg-white/85 text-accent hover:bg-white"
            }`}
          >
            <Heart size={16} fill={favored ? "currentColor" : "none"} strokeWidth={2.2} />
          </button>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-display font-bold text-xl text-accent">{breed.name}</h3>
        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{breed.summary}</p>
        <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="capitalize">{breed.size}</span>
          <span className="w-1 h-1 rounded-full bg-muted-foreground/50" />
          <span>Energy {breed.energy}/5</span>
          <span className="w-1 h-1 rounded-full bg-muted-foreground/50" />
          <span>{breed.lifespan_years}y</span>
        </div>
        {breed.purchase_price && (
          <div
            data-testid={`card-price-${breed.id}`}
            className="mt-3 inline-flex items-center gap-1.5 self-start rounded-full bg-secondary text-secondary-foreground px-3 py-1 text-xs font-semibold"
          >
            <Tag size={11} />
            Puppy price ${breed.purchase_price.min_aud.toLocaleString()}–${breed.purchase_price.max_aud.toLocaleString()}
          </div>
        )}
        {reasons?.length > 0 && (
          <ul className="mt-3 text-xs text-accent/80 space-y-1">
            {reasons.slice(0, 2).map((r) => (
              <li key={r} className="flex gap-2"><span className="text-primary">•</span>{r}</li>
            ))}
          </ul>
        )}
      </div>
    </Link>
  );
}
